import { useState, useEffect, useCallback } from 'react';
import { sapCDCService } from '../services/SAPCDCService';

/**
 * React Hook for SAP CDC Authentication
 * Provides SAP CDC login, logout, and authentication state management
 */
export const useSAPCDC = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [showLocalhostHelper, setShowLocalhostHelper] = useState(false);

  /**
   * Initialize SAP CDC service - prevent duplicate initializations
   */
  const initialize = useCallback(async () => {
    // Prevent multiple concurrent initializations
    if (isInitialized || isLoading) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);

      await sapCDCService.init();
      setIsInitialized(true);

      // Check if user is already authenticated
      const authStatus = await sapCDCService.isAuthenticated();
      setIsAuthenticated(authStatus);

      if (authStatus) {
        const userInfo = await sapCDCService.getUserInfo();
        setUser(userInfo);
        
        const tokenInfo = await sapCDCService.getToken();
        setToken(tokenInfo);
      }

      // Silent success - only log errors
    } catch (err) {
      console.error('❌ [useSAPCDC] Initialization failed:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, isLoading]); // Add dependencies

  /**
   * Perform SAP CDC login
   */
  const login = useCallback(async (loginOptions = {}) => {
    try {
      console.log('🔐 [useSAPCDC] Starting login process...');
      setIsLoading(true);
      setError(null);

      if (!isInitialized) {
        console.log('⚠️ [useSAPCDC] Not initialized, initializing first...');
        await initialize();
      }

      const response = await sapCDCService.login(loginOptions);
      
      if (response.user) {
        setIsAuthenticated(true);
        setUser(response.user);
        
        // Get token information
        const tokenInfo = await sapCDCService.getToken();
        setToken(tokenInfo);
        
        console.log('✅ [useSAPCDC] Login successful');
        return {
          success: true,
          user: response.user,
          token: tokenInfo
        };
      }
    } catch (err) {
      console.error('❌ [useSAPCDC] Login failed:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, initialize]);

  /**
   * Perform SAP CDC registration
   */
  const register = useCallback(async (userDetails) => {
    try {
      console.log('📝 [useSAPCDC] Starting registration process...');
      setIsLoading(true);
      setError(null);

      if (!isInitialized) {
        console.log('⚠️ [useSAPCDC] Not initialized, initializing first...');
        await initialize();
      }

      const response = await sapCDCService.register(userDetails);
      
      if (response.success && response.user) {
        console.log('✅ [useSAPCDC] Registration successful');
        
        // After successful registration, automatically log the user in
        const loginResult = await login({
          loginID: userDetails.email,
          password: userDetails.password
        });
        
        return {
          success: true,
          user: response.user,
          UID: response.UID,
          loginResult: loginResult
        };
      } else {
        return {
          success: false,
          error: 'Registration failed'
        };
      }
    } catch (err) {
      console.error('❌ [useSAPCDC] Registration failed:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, initialize, login]);

  /**
   * Perform SAP CDC logout
   */
  const logout = useCallback(async () => {
    try {
      console.log('🚪 [useSAPCDC] Starting logout process...');
      setIsLoading(true);
      setError(null);

      await sapCDCService.logout();
      
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      
      console.log('✅ [useSAPCDC] Logout successful');
      return { success: true };
    } catch (err) {
      console.error('❌ [useSAPCDC] Logout failed:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh user information and token
   */
  const refreshUserInfo = useCallback(async () => {
    try {
      console.log('🔄 [useSAPCDC] Refreshing user information...');
      setIsLoading(true);

      const authStatus = await sapCDCService.isAuthenticated();
      setIsAuthenticated(authStatus);

      if (authStatus) {
        const userInfo = await sapCDCService.getUserInfo();
        setUser(userInfo);
        
        const tokenInfo = await sapCDCService.getToken();
        setToken(tokenInfo);
        
        console.log('✅ [useSAPCDC] User information refreshed');
        return { success: true, user: userInfo, token: tokenInfo };
      } else {
        setUser(null);
        setToken(null);
        console.log('ℹ️ [useSAPCDC] User not authenticated');
        return { success: true, user: null, token: null };
      }
    } catch (err) {
      console.error('❌ [useSAPCDC] Failed to refresh user info:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get current access token
   */
  const getAccessToken = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }

      const tokenInfo = await sapCDCService.getToken();
      setToken(tokenInfo);
      return tokenInfo;
    } catch (err) {
      console.error('❌ [useSAPCDC] Failed to get access token:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated]);

  /**
   * Check authentication status
   */
  const checkAuthStatus = useCallback(async () => {
    try {
      const authStatus = await sapCDCService.isAuthenticated();
      setIsAuthenticated(authStatus);
      return authStatus;
    } catch (err) {
      console.error('❌ [useSAPCDC] Failed to check auth status:', err);
      setError(err.message);
      return false;
    }
  }, []);

  /**
   * Initiate forgot password flow
   */
  const forgotPassword = useCallback(async (email) => {
    try {
      console.log('🔐 [useSAPCDC] Starting forgot password process...');
      setIsLoading(true);
      setError(null);

      if (!isInitialized) {
        console.log('⚠️ [useSAPCDC] Not initialized, initializing first...');
        await initialize();
      }

      const response = await sapCDCService.forgotPassword(email);
      
      console.log('✅ [useSAPCDC] Forgot password request successful');
      return {
        success: true,
        message: 'Password reset email sent successfully. Please check your email for instructions.',
        response: response
      };
    } catch (err) {
      console.error('❌ [useSAPCDC] Forgot password failed:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, initialize]);

  /**
   * Reset password using reset token and new password
   */
  const resetPassword = useCallback(async (resetToken, newPassword) => {
    try {
      console.log('🔄 [useSAPCDC] Starting password reset process...');
      setIsLoading(true);
      setError(null);

      if (!isInitialized) {
        console.log('⚠️ [useSAPCDC] Not initialized, initializing first...');
        await initialize();
      }

      const response = await sapCDCService.resetPassword(resetToken, newPassword);
      
      console.log('✅ [useSAPCDC] Password reset successful');
      return {
        success: true,
        message: 'Password reset successful. You can now login with your new password.',
        response: response
      };
    } catch (err) {
      console.error('❌ [useSAPCDC] Password reset failed:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    } finally {
      setIsLoading(false);
    }
  }, [isInitialized, initialize]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Return hook interface
  return {
    // State
    isInitialized,
    isAuthenticated,
    user,
    isLoading,
    error,
    token,
    showLocalhostHelper,
    
    // Actions
    initialize,
    login,
    register,
    logout,
    refreshUserInfo,
    getAccessToken,
    checkAuthStatus,
    forgotPassword,
    resetPassword,
    
    // Utility functions
    clearError: () => setError(null),
    
    // Helper for localhost development
    displayLocalhostHelper: () => setShowLocalhostHelper(true)
  };
};
